import {
  EKeyboardArea,
  KEYBOARD_AREA_ATTRIBUTE,
  activeKeyboardArea,
  findKeyboardArea,
} from './keyboard-area.ts';

const NON_TEXT_INPUT_TYPE_LIST = [
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
];

// Widgets whose roles move between their items or values with the arrow keys
const ARROW_KEY_WIDGET_SELECTOR = [
  'combobox',
  'listbox',
  'option',
  'tab',
  'tablist',
  'radiogroup',
  'slider',
  'spinbutton',
  'tree',
  'treeitem',
  'menu',
  'menubar',
  'menuitem',
  'grid',
].map((role) => `[role="${role}"]`).join(', ');

/** Whether the element takes text, so that the keys move its caret or change its value. */
export function isEditableElement(element: Element): boolean {
  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPE_LIST.includes(element.type);
  }
  return element instanceof HTMLTextAreaElement
    || element instanceof HTMLSelectElement
    || (element instanceof HTMLElement && element.isContentEditable)
    || element.closest('[role="combobox"], [role="listbox"]') !== null;
}

/** Whether the element uses the arrow keys itself, e.g. a text field, a radio button or the tabs of a tab view. */
export function doesElementUseArrowKeys(element: Element): boolean {
  return isEditableElement(element)
    || (element instanceof HTMLInputElement && element.type === 'radio')
    || element.closest(ARROW_KEY_WIDGET_SELECTOR) !== null;
}

/**
 * Whether nothing on the page has the focus, or only an area of the app layout as a whole, e.g. after
 * switching to it, so that the keys reach the page itself.
 */
export function isNothingFocused(element: Element): boolean {
  return element === document.body
    || element === document.documentElement
    || element.hasAttribute(KEYBOARD_AREA_ATTRIBUTE);
}

/**
 * Whether the key event applies to the area of the app layout: it comes from an element in the area
 * or, while nothing has the focus, the area is the one focused or clicked last.
 */
export function isKeyboardEventInArea(event: KeyboardEvent, area: EKeyboardArea): boolean {
  if (!(event.target instanceof Element)) {
    return false;
  }
  const isDocumentTarget = event.target === document.body || event.target === document.documentElement;
  const eventArea = isDocumentTarget
    ? activeKeyboardArea.value
    : findKeyboardArea(event.target);
  return eventArea === area;
}
